import {
    inject,
    Injectable,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { LOCALSTORAGE } from './local-storage.service';

const FEATURE_FLAGS = {
    SELF_BOOKING: false,
};

export type FeatureFlag = keyof typeof FEATURE_FLAGS;
export type FeatureFlagValue<F extends FeatureFlag> = (typeof FEATURE_FLAGS)[F];

export const FeatureFlags: Record<FeatureFlag, FeatureFlag> = Object.assign(
    {},
    ...Object.keys(FEATURE_FLAGS).map(key => ({ [key]: key }))
);

@Injectable({
    providedIn: 'root',
})
export class FeatureFlagService {
    private localStorage = inject<Storage>(LOCALSTORAGE);

    private featureFlagSignals: {
        [K in FeatureFlag]: WritableSignal<FeatureFlagValue<K>>;
    } = Object.assign(
        {},
        ...Object.entries(FEATURE_FLAGS).map(([key, value]) => ({
            [key]: signal(value),
        }))
    );

    constructor() {
        Object.entries(this.featureFlagSignals).forEach(([key, signal]) => {
            const storedValue = this.localStorage.getItem(
                `feature_flag_${key}`
            );
            if (storedValue !== null) {
                (signal as WritableSignal<boolean>).set(storedValue === 'true');
            }
        });
    }

    getFeatureFlag<F extends FeatureFlag>(
        flag: F
    ): Signal<FeatureFlagValue<F>> {
        return this.featureFlagSignals[flag].asReadonly();
    }

    setFeatureFlag<F extends FeatureFlag>(
        flag: F,
        state: FeatureFlagValue<F>,
        persist = false
    ): void {
        this.featureFlagSignals[flag].set(state);
        if (persist) {
            this.localStorage.setItem(`feature_flag_${flag}`, state.toString());
        }
    }
}

type FeatureFlagResolverData = {
    [F in FeatureFlag]: {
        featureFlag: F;
        value?: FeatureFlagValue<F>;
        persist?: boolean;
    };
}[FeatureFlag];

@Injectable({ providedIn: 'root' })
export class FeatureFlagResolver {
    private featureFlagService = inject(FeatureFlagService);

    resolve(route: ActivatedRouteSnapshot) {
        if (!route.data['featureFlags']) {
            return;
        }
        route.data['featureFlags'].forEach(
            ({ featureFlag, value, persist }: FeatureFlagResolverData) => {
                if (value === undefined) {
                    this.featureFlagService.setFeatureFlag(
                        featureFlag,
                        FEATURE_FLAGS[featureFlag]
                    );
                } else {
                    this.featureFlagService.setFeatureFlag(
                        featureFlag,
                        value,
                        persist
                    );
                }
            }
        );
    }
}
